// Who moved a tenant, for a lifecycle row (spec 15 §4 S3 and S5).
//
// A staff actor is named where this role may read the staff directory (admin,
// readonly; spec 15 §0.1) and reads "platform staff" otherwise; a system actor
// reads "automatically". A raw id is never shown.

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { listStaff, type PlatformRole } from "../lib/platform-api";
import { canReadStaff, lifecycleActor } from "../lib/tenants";

export function useActorName(role: PlatformRole): (actor: string) => string {
  const { t } = useTranslation();
  const staff = useQuery({
    queryKey: ["platform", "staff"],
    queryFn: ({ signal }) => listStaff(signal),
    enabled: canReadStaff(role),
    staleTime: 5 * 60_000,
  });
  return (raw: string) => {
    const actor = lifecycleActor(raw);
    if (actor.kind === "system") return t("admin.tenant.automatic");
    const member = staff.data?.find((s) => s.id === actor.staffId);
    const name =
      member !== undefined
        ? member.display_name.trim() !== ""
          ? member.display_name
          : member.email
        : t("admin.tenant.byStaff");
    return t("admin.tenant.by", { who: name });
  };
}
